import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { toast, Toaster } from 'sonner';
import { ChefHat, Clock, ArrowLeft, Share2, Utensils } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK_IMG = 'https://images.pexels.com/photos/35041652/pexels-photo-35041652.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

export default function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/recipes/database/${id}`);
        if (active) setRecipe(res.data);
      } catch (e) {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: recipe?.name, text: `Check out this recipe: ${recipe?.name}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Recipe link copied to clipboard! 🔗');
      }
    } catch (e) {
      // user cancelled share; ignore
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50" data-testid="recipe-detail-loading">
        <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-purple-50 to-pink-50 p-6" data-testid="recipe-detail-error">
        <p className="text-lg text-gray-700">Sorry, we couldn't find that recipe.</p>
        <Button onClick={() => navigate('/')} className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
          Back to Home
        </Button>
      </div>
    );
  }

  const n = recipe.nutritional_info || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 pb-16" data-testid="recipe-detail-page">
      <Toaster position="top-center" richColors />

      {/* Hero image */}
      <div className="relative h-72 md:h-96 w-full overflow-hidden">
        <img
          src={recipe.image_url || FALLBACK_IMG}
          alt={recipe.name}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = FALLBACK_IMG; }}
          data-testid="recipe-detail-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
        <div className="absolute top-4 left-4 right-4 flex justify-between">
          <Button onClick={() => navigate('/')} variant="secondary" className="bg-white/90 hover:bg-white text-gray-800 gap-2" data-testid="recipe-detail-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <Button onClick={handleShare} className="bg-white/90 hover:bg-white text-purple-700 gap-2" data-testid="recipe-detail-share-btn">
            <Share2 className="w-4 h-4" /> Share
          </Button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <h1 className="text-3xl md:text-5xl font-bold font-serif drop-shadow-lg" data-testid="recipe-detail-name">{recipe.name}</h1>
          <div className="flex gap-3 mt-3 flex-wrap">
            <Badge className="bg-white/20 backdrop-blur text-white border-white/30 gap-1"><ChefHat className="w-3 h-3" /> {recipe.chef_name}</Badge>
            <Badge className="bg-white/20 backdrop-blur text-white border-white/30">📍 {recipe.region?.charAt(0).toUpperCase() + recipe.region?.slice(1)}</Badge>
            <Badge className="bg-white/20 backdrop-blur text-white border-white/30 gap-1"><Clock className="w-3 h-3" /> {recipe.cooking_time}</Badge>
            <Badge className="bg-white/20 backdrop-blur text-white border-white/30">📊 {recipe.difficulty}</Badge>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-6 relative z-10 max-w-5xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Ingredients */}
          <Card className="bg-white/95 backdrop-blur-xl shadow-xl lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-xl text-purple-700 flex items-center gap-2"><Utensils className="w-5 h-5" /> Ingredients</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2" data-testid="recipe-detail-ingredients">
                {recipe.ingredients?.map((ing, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 w-2 h-2 rounded-full bg-pink-400 flex-shrink-0"></span>
                    {ing}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card className="bg-white/95 backdrop-blur-xl shadow-xl lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-xl text-purple-700">👨‍🍳 Cooking Instructions</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-4" data-testid="recipe-detail-instructions">
                {recipe.instructions?.map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white flex items-center justify-center text-sm font-bold">{i + 1}</span>
                    <p className="text-sm text-gray-700 pt-1">{step}</p>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>

        {/* Nutrition + tips */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <Card className="bg-white/95 backdrop-blur-xl shadow-xl">
            <CardHeader>
              <CardTitle className="text-xl text-purple-700">🥗 Nutrition</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 text-sm" data-testid="recipe-detail-nutrition">
                {n.calories && <div><span className="font-semibold text-gray-800">Calories:</span> {n.calories}</div>}
                {n.protein && <div><span className="font-semibold text-gray-800">Protein:</span> {n.protein}</div>}
                {n.carbs && <div><span className="font-semibold text-gray-800">Carbs:</span> {n.carbs}</div>}
                {n.fat && <div><span className="font-semibold text-gray-800">Fat:</span> {n.fat}</div>}
                {n.fiber && <div><span className="font-semibold text-gray-800">Fiber:</span> {n.fiber}</div>}
                {n.vitamins && <div className="col-span-2"><span className="font-semibold text-gray-800">Vitamins:</span> {n.vitamins}</div>}
              </div>
            </CardContent>
          </Card>

          {recipe.chef_tips && (
            <Card className="bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-xl">
              <CardHeader>
                <CardTitle className="text-xl text-white">💡 Chef's Tip</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-white/95 italic" data-testid="recipe-detail-tips">{recipe.chef_tips}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
